import React from 'react';

/**
 * Colors matched to Heroku CLI / terminal screenshot:
 * - router stamp+source: lime green
 * - app stamp+source: sky blue
 * - keys: off-white
 * - HTTP methods: magenta
 * - paths / status 2xx: lime green
 * - dyno / service timings: cyan
 * - app message body: white
 */
const C = {
  router: '#39ff14',
  app: '#7dd3fc',
  key: '#e8e8e8',
  method: '#ff2bd6',
  path: '#39ff14',
  status: '#39ff14',
  metric: '#67e8f9',
  msg: '#f5f5f5',
};

function kv(key, value, valueColor = C.key) {
  return [
    { t: `${key}=`, c: C.key },
    { t: value, c: valueColor },
  ];
}

function joinParts(parts) {
  const out = [];
  parts.forEach((chunk, i) => {
    if (i > 0) out.push({ t: ' ', c: C.key });
    if (Array.isArray(chunk)) out.push(...chunk);
    else out.push(chunk);
  });
  return out;
}

/** Sample lines styled like real `heroku logs --tail` output. */
const HEROKU_LOG_TEMPLATES = [
  {
    sourceKind: 'router',
    source: 'heroku[router]',
    message: joinParts([
      kv('at', 'info'),
      kv('method', 'POST', C.method),
      kv('path', '"/api/workspace/.../generate-voiceover"', C.path),
      kv('host', 'proto-backend-d2c1638486b9.herokuapp.com'),
      kv('request_id', '865c82ca-25a6-3abe-b2d2-0751b18dbe32'),
      kv('fwd', '"109.48.217.204"'),
      kv('dyno', 'web.1', C.metric),
      kv('connect', '0ms', C.metric),
      kv('service', '9663ms', C.metric),
      kv('status', '200', C.status),
      kv('bytes', '30391'),
      kv('protocol', 'http2.0'),
      kv('tls', 'true'),
      kv('tls_version', 'tls1.3'),
    ]),
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: '[AUTH] Token received for: POST /workspace/.../generate-voiceover', c: C.msg }],
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: '[WORKSPACE VOICEOVER] Starting voiceover generation', c: C.msg }],
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: "[WORKSPACE VOICEOVER] Request body: { voiceGenerationMode: 'expressive' }", c: C.msg }],
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: '[WORKSPACE VOICEOVER] ElevenLabs model: eleven_v4 (preference: v4)', c: C.msg }],
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: '[WORKSPACE VOICEOVER] Calling ElevenLabs API with voice: aOH45H1qc0YRO1uI9EfZ', c: C.msg }],
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: '[WORKSPACE VOICEOVER] ElevenLabs response received, audio length: 687740', c: C.msg }],
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: '[WORKSPACE VOICEOVER] Voiceover generation completed', c: C.msg }],
  },
  {
    sourceKind: 'router',
    source: 'heroku[router]',
    message: joinParts([
      kv('at', 'info'),
      kv('method', 'PATCH', C.method),
      kv('path', '"/api/workspace/.../script/.../content"', C.path),
      kv('host', 'proto-backend-d2c1638486b9.herokuapp.com'),
      kv('request_id', 'afaf912b-e86a-e2a2-1665-d6530980604b'),
      kv('fwd', '"109.48.217.204"'),
      kv('dyno', 'web.1', C.metric),
      kv('connect', '0ms', C.metric),
      kv('service', '171ms', C.metric),
      kv('status', '200', C.status),
      kv('bytes', '645'),
      kv('protocol', 'http2.0'),
      kv('tls', 'true'),
      kv('tls_version', 'tls1.3'),
    ]),
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: '[VOICE MANAGEMENT] At voice limit (30/30 custom), cleaning up...', c: C.msg }],
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: '[VOICE MANAGEMENT] Skipping protected voice: "Boukar\'s Spaceship"', c: C.msg }],
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: '[VOICE MANAGEMENT] Cleanup complete. Deleted 1 voices', c: C.msg }],
  },
  {
    sourceKind: 'router',
    source: 'heroku[router]',
    message: joinParts([
      kv('at', 'info'),
      kv('method', 'OPTIONS', C.method),
      kv('path', '"/api/workspace/.../cuts"', C.path),
      kv('host', 'proto-backend-d2c1638486b9.herokuapp.com'),
      kv('request_id', 'a3bf442e-c400-bf77-e340-628f71261356'),
      kv('fwd', '"109.48.217.204"'),
      kv('dyno', 'web.1', C.metric),
      kv('connect', '0ms', C.metric),
      kv('service', '1ms', C.metric),
      kv('status', '200', C.status),
      kv('bytes', '0'),
      kv('protocol', 'http2.0'),
      kv('tls', 'true'),
      kv('tls_version', 'tls1.3'),
    ]),
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: 'Lambda updating progress for script 6ac3f9125cce10c4861cdaf7: 42%', c: C.msg }],
  },
  {
    sourceKind: 'router',
    source: 'heroku[router]',
    message: joinParts([
      kv('at', 'info'),
      kv('method', 'GET', C.method),
      kv('path', '"/api/workspace/.../cuts?page=1&pageSize=33"', C.path),
      kv('host', 'proto-backend-d2c1638486b9.herokuapp.com'),
      kv('request_id', '4e0b4ac9-7c66-bb69-86b1-0451f8c0853c'),
      kv('fwd', '"109.48.217.204"'),
      kv('dyno', 'web.1', C.metric),
      kv('connect', '0ms', C.metric),
      kv('service', '428ms', C.metric),
      kv('status', '304', C.status),
      kv('bytes', '0'),
      kv('protocol', 'http2.0'),
      kv('tls', 'true'),
      kv('tls_version', 'tls1.3'),
    ]),
  },
  {
    sourceKind: 'app',
    source: 'app[web.1]',
    message: [{ t: "ElevenLabs API response: { voice_id: 'fROtw79YPO5UEuwVpRsZ' }", c: C.msg }],
  },
  {
    sourceKind: 'app',
    source: 'app[api]',
    message: [{ t: 'Log session created by user boukar.o.sall@gmail.com', c: C.msg }],
  },
];

const MAX_VISIBLE = 22;

function formatStamp(ms) {
  return new Date(ms).toISOString().replace('Z', '+00:00');
}

function randomDelayMs() {
  const roll = Math.random();
  if (roll < 0.35) return 120 + Math.floor(Math.random() * 220);
  if (roll < 0.8) return 350 + Math.floor(Math.random() * 550);
  return 900 + Math.floor(Math.random() * 1400);
}

/**
 * Tiny Heroku-style log tail. Parent should be the studio preview grey rect
 * (`rgba(0,0,0,0.04)` / `rgba(255,255,255,0.04)`), already `position: relative`.
 * Cards move behind; this stays pinned to the rect's top-left.
 */
export default function CarouselHerokuLogsOverlay() {
  const [lines, setLines] = React.useState([]);
  const seqRef = React.useRef(0);

  React.useEffect(() => {
    setLines([]);
    seqRef.current = 0;
    let cancelled = false;
    let timeoutId = null;

    const pushLine = () => {
      if (cancelled) return;
      const i = seqRef.current++;
      const tpl = HEROKU_LOG_TEMPLATES[i % HEROKU_LOG_TEMPLATES.length];
      const next = {
        id: `log-${i}`,
        stamp: formatStamp(Date.now()),
        source: tpl.source,
        sourceKind: tpl.sourceKind,
        message: tpl.message,
      };
      setLines((prev) => {
        const merged = [...prev, next];
        return merged.length > MAX_VISIBLE ? merged.slice(merged.length - MAX_VISIBLE) : merged;
      });
      timeoutId = setTimeout(pushLine, randomDelayMs());
    };

    timeoutId = setTimeout(pushLine, 120 + Math.floor(Math.random() * 400));

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div
      className="carousel-heroku-logs"
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 12,
        overflow: 'hidden',
        pointerEvents: 'none',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'stretch',
        padding: '8px 10px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        {lines.map((line) => {
          const headColor = line.sourceKind === 'router' ? C.router : C.app;
          return (
            <div
              key={line.id}
              style={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 12,
                lineHeight: 1.4,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                marginBottom: 6,
                color: '#f5f5f5',
                textShadow: '0 1px 1px rgba(0,0,0,0.85)',
              }}
            >
              <span style={{ color: headColor }}>{line.stamp} {line.source}:</span>
              {' '}
              {line.message.map((seg, idx) => (
                <span key={idx} style={{ color: seg.c }}>{seg.t}</span>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Optional inject for carousel style tags; layout is inline. */
export const CAROUSEL_HEROKU_LOGS_CSS = '';
